import React, { useEffect, useRef } from 'react'

import { useAuth0 } from '@auth0/auth0-react'

import { Header } from '~/components'
import {
  NameDobCollectionParams,
  NameAndDemographicCollectionParams,
  SignupType,
  useBffWebOnboardingCreateNewAccount,
  useBffWebOnboardingCreateMemberAccount,
} from '../signup/steps/account/utils'
import Loader from '~/components/Loader/Loader'
import { useSetToken } from '~/utils'
import { useUserAuthenticated } from '~/utils/useUserAuthenticated'
import { consolidateSignupDraft } from '~/utils/signupDraft'

const AccountCreation: React.FC = () => {
  const { user } = useAuth0()
  const prospectCreation = useBffWebOnboardingCreateNewAccount()
  const memberCreation = useBffWebOnboardingCreateMemberAccount()
  const setToken = useSetToken()
  const userAuthenticated = useUserAuthenticated()
  const hasRun = useRef(false)

  useEffect(() => {
    const email = user?.email
    if (!email || hasRun.current) return
    hasRun.current = true

    const createAccount = async () => {
      // Key the collected signup data by the user's email first, so the flow can
      // be resumed by email after an email-verification / re-login detour.
      const draft = consolidateSignupDraft(email)

      // Bootstrap the lucian User; bail if the email-verification gate redirected
      // to /emailverification.
      await setToken.handler()
      const authedUser = await userAuthenticated()
      if (!authedUser || !draft) return

      const signupType = draft.signupType as SignupType
      const insurancePayerId = draft.insurancePayerId ? Number(draft.insurancePayerId) : null

      if (signupType === SignupType.HEALTH_PLAN_PROSPECT) {
        const params: NameDobCollectionParams = {
          firstName: draft.firstName || '',
          lastName: draft.lastName || '',
          preferredName: draft.preferredName || '',
          dob: draft.dob || '',
          consentForms: JSON.parse(draft.consentForms || '[]') as number[],
          insurancePayerId,
          inviteCode: draft.inviteCode ?? null,
          signupType,
        }

        await prospectCreation.handler(params)
      } else {
        const sexOption = JSON.parse(draft.sex || 'null')
        const genderOption = JSON.parse(draft.gender || 'null')
        const pronounsOption = JSON.parse(draft.pronouns || 'null')
        const preferredLanguageOption = JSON.parse(draft.preferredLanguage || 'null')

        const params: NameAndDemographicCollectionParams = {
          firstName: draft.firstName || '',
          lastName: draft.lastName || '',
          preferredName: draft.preferredName || '',
          dob: draft.dob || '',
          consentForms: JSON.parse(draft.consentForms || '[]') as number[],
          insurancePayerId,
          inviteCode: null,
          state: JSON.parse(draft.state || 'null'),
          sex: sexOption?.label || '',
          gender: genderOption ? [genderOption.value] : null,
          pronouns: pronounsOption?.label || null,
          preferredLanguage: preferredLanguageOption?.value
            ? Number(preferredLanguageOption.value)
            : null,
          preferredLanguageOther: draft.preferredLanguageOther || null,
          signupType,
        }

        await memberCreation.handler(params)
      }
    }
    createAccount()
  }, [user, setToken, userAuthenticated, prospectCreation, memberCreation])

  return (
    <>
      <Header />
      <Loader />
    </>
  )
}

export default AccountCreation
